/**
 * This module helps us build <select> elements from <input> elements in order to
 * send the correct state abbreviations to Stripe for credit card processing.
 *
 * This module will search the order page for country <input> and <select> elements,
 * and ensure they are all <select> elements if required. Then, as a country <select>
 * element is changed, we look for the nearest state <select> or <input> element, and
 * populate it with the correct states for the given country.
 *
 * As of November 2019, only US, CA, and AU (Australia) are required to use valid
 * state abbreviations according to Stripe, but more will be added in 2020.
 */

;(function () {
  /**
   * Store details about the state elements altered here.
   */
  var attributeStorage = {}

  /**
   * Returns whether or not the feature is enabled for this particular page.
   * We currently check a feature gate <input> on the page that was added from the editor,
   * as well as making sure we are on an order page that has a ".StripeElement" on the page.
   *
   * In the future, we will remove the feature gate check, and only check for ".StripeElement"
   *
   * @returns {bool}
   */
  function isEnabled() {
    var $featureEnabled = document.querySelector(
      'input[type="hidden"][name="cf-state-county-dropdown-feature-enabled"]'
    )
    var isAStripeOrderPage = document.querySelectorAll('.StripeElement').length > 0
    if (!isAStripeOrderPage) {
      // Do not continue processing if this is not a Stripe order page.
      return false
    }
    if ($featureEnabled && $featureEnabled.value === 'true') {
      return true
    } else {
      return false
    }
  }

  /**
   * A country is eligible if it can be found in the ClickFunnels.stateOptions object.
   * @param {string} countryCode
   * @returns {boolean} returns whether or not the given countryCode was found iin the stateOptions object
   */
  function countryIsEligibleForStateDropdown(countryCode) {
    return window.ClickFunnels.stateOptions[countryCode] !== undefined
  }

  /**
   * This function is called on page load, and converts the nearest state <input> elements
   * to <select> if the country <select> value is one of the currently valid
   * countries from window.ClickFunnels.countryOptions
   *
   * @param {*} $el - The country <select> element
   */
  function setupCountrySelect($el) {
    var $countrySelect = $el
    var countryCode = $el.value
    var $stateElement = findClosestStateElement($countrySelect)
    var $selectEl = null

    if (!countryIsEligibleForStateDropdown(countryCode)) {
      if ($stateElement.tagName === 'SELECT') {
        convertToInput($stateElement)
      }
      return
    }

    if ($stateElement && $stateElement.tagName !== 'SELECT') {
      $selectEl = convertToSelect($stateElement)
      // add new <select> element below current <input> element
      insertAfter($selectEl, $stateElement)
      removeNode($stateElement)
    } else {
      $selectEl = $stateElement
    }

    if ($selectEl) {
      populateSelect($selectEl, countryCode)
    }
  }

  function addWatchersToCountrySelectors($countrySelectElements) {
    /* We want to watch all existing country selectors
       If they change to a eligible country (e.g. US, Canada, Australia) we 
       need to convert all state <input>s to <select>
    */

    $countrySelectElements.each(function () {
      var $countrySelect = this
      $countrySelect.addEventListener('change', function () {
        setupCountrySelect(this)
      })
    })
  }

  /**
   * Find the closest state element to the incoming $countrySelect element
   *
   * @param {Node} $countrySelect
   *
   * @returns {Node,null} returns the state element if found
   */
  function findClosestStateElement($countrySelect) {
    // Check for the parent element in this order:
    // 1. if in an order2step, find a state inside that element
    // 2. if in a billing address element, fine a state inside that element
    // 3. check the col-* for the state element inside it
    $countrySelect = $($countrySelect)
    var $parentOrder2Step = $countrySelect.parents('.elOrder2Step')
    var $parentBilling = $countrySelect.parents('.elShippingForm')
    var $currentColumn = $countrySelect.closest('[class^="col-"]')

    var $parentElement = null

    if ($parentOrder2Step.length > 0) {
      $parentElement = $parentOrder2Step
    } else if ($parentBilling.length > 0) {
      $parentElement = $parentBilling
    } else {
      $parentElement = $currentColumn
    }

    var $stateElement = $parentElement.find('[name="state"], [name="shipping_state"]')[0]
    return $stateElement
  }

  /**
   * Populates the $selectEl with states, if we have states defined for the countryCode
   * @param {Node} $selectEl
   * @param {string} countryCode
   */
  function populateSelect($selectEl, countryCode) {
    clearSelect($selectEl)
    var i = 0
    var states = window.ClickFunnels.stateOptions[countryCode]
    if (states === undefined) {
      return
    }
    var len = states.length
    var option = null
    for (; i < len; i++) {
      option = document.createElement('option')
      option.value = states[i].code
      option.innerHTML = states[i].name

      $selectEl.appendChild(option)
    }
  }

  /**
   * Clears a <select> element of all <option> elements, and sets the value attribute to ""
   * @param {Node} $selectEl The <select> element to clear or reset.
   */
  function clearSelect($selectEl) {
    $selectEl.innerHTML = ''
    $selectEl.value = ''
  }

  /**
   * Copy the HTML DOM attributes from one Node to another Node.
   * @param {Node} from Copy attributes from this node
   * @param {Node} to This node will receive the attributes from the "from" node
   */
  function copyAttributes(from, to) {
    var i = 0
    var len = from.attributes.length
    var attribute = null
    var attributeName = null
    for (; i < len; i++) {
      attribute = from.attributes[i]
      attributeName = attribute.name.toLowerCase()
      // Ignore "type" and "placeholder" attributes.
      if (attributeName === 'type' || attributeName === 'placeholder') {
        continue
      }
      to.setAttribute(attribute.name, attribute.value)
    }
  }

  /**
   * Inserts newNode after the referenceNode in the DOM tree.
   * @param {Node} newNode
   * @param {Node} referenceNode
   */
  function insertAfter(newNode, referenceNode) {
    referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling)
  }

  /**
   * Removes the node from the DOM tree
   *
   * @param {Node} node
   */
  function removeNode(node) {
    if (node && node.parentNode) {
      return node.parentNode.removeChild(node)
    }
  }

  /**
   * Converts the given DOM Node element to a <select> by taking the
   * attributes of the original DOM Node element, and creating a new
   * <select> element, and inserting it after the incoming element,
   * then finishing by deleting the original $el.
   *
   * @param {Node} $el - The DOM Node to convert to a <select> element
   *
   * @return {Node} The new <select> element that was created
   */
  function convertToSelect($el) {
    // Only convert to <select> if it's an <input>. Otherwise, leave it alone for now.
    if ($el.tagName !== 'INPUT') {
      return
    }
    // Clear its value
    $el.value = ''
    // Create a new <select> tag
    var $selectEl = document.createElement('select')
    // Copy the attributes (including CSS classes)
    copyAttributes($el, $selectEl)

    return $selectEl
  }

  /**
   * Converts a <select> element to an <input> element
   * @param {Node} $el A <select> node to convert back to an <input>
   */
  function convertToInput($el) {
    if ($el.tagName !== 'SELECT') {
      return
    }
    // Clear its value
    $el.value = ''
    // Create a new <input> tag
    var $inputEl = document.createElement('input')

    // Retrieve any attrs from storage that were stored on page load for all state <input> elements
    copyInputAttributesFromStorage($el, $inputEl)
    // Copy the attributes (including CSS classes)
    copyAttributes($el, $inputEl)
    insertAfter($inputEl, $el)
    removeNode($el)

    return $inputEl
  }

  /**
   * Find all state <input> elements, and store all attributes in an in-memory storage object.
   * Also, add a UUID attribute to each <input> element, so we can look up and find those
   * stored attributes later on.
   */
  function storeStateElementAttrs() {
    var $stateElements = $('input[name="state"], input[name="shipping_state"]')
    $stateElements.each(function () {
      var uuid = window.CFUtils.generateUUID()
      attributeStorage[uuid] = {}
      this.setAttribute('data-cf-input-attr-id', uuid)
      var i = 0
      var len = this.attributes.length
      var attribute = null
      for (; i < len; i++) {
        attribute = this.attributes[i]
        attributeStorage[uuid][attribute.name] = attribute.value
      }
    })
  }

  /**
   * Lookup the attributes stored in $fromEl's UUID attribute, then
   * copy the attributes to the $toEl.
   *
   * @param {Node} $fromEl The element to use to lookup the UUID in the storage
   * @param {Node} $toEl The element to receive all attributes from storage
   *
   * @returns {null}
   */
  function copyInputAttributesFromStorage($fromEl, $toEl) {
    var uuid = $fromEl.getAttribute('data-cf-input-attr-id')
    if (!uuid) {
      return
    }
    var attributes = attributeStorage[uuid]
    for (var attributeName in attributes) {
      $toEl.setAttribute(attributeName, attributes[attributeName])
    }
  }

  /**
   * Find all country <select> elements and populate them with the countries in window.ClickFunnels.countryOptions.
   * Optionally, you can pass in the jQuery query of country elements you want to populate.
   *
   * @param {Node} [$countrySelection] A jQuery query of country elements, or NULL
   */
  function populateCountrySelectors($countrySelection) {
    //Replaces content for select country in billing and shipping elements.
    //The new content is based on a list defined in window.ClickFunnels.countryOptions
    $countrySelection = $countrySelection || $('select[name="country"], select[name="shipping_country"]')
    $countrySelection.each(function () {
      $this = $(this)
      $this.html('')
      window.ClickFunnels.countryOptions.forEach(function (countryData) {
        $this.append('<option value="' + countryData['code'] + '">' + countryData['name'] + '</option>')
      })
    })
  }

  function init() {
    // only convert country elements if this is an order page, and it's got a stripe element on it.
    if (!isEnabled()) {
      return
    }
    var $countryFormElements = $('[name="country"], [name="shipping_country"]')
    var $selectEl = null
    $countryFormElements.each(function () {
      if (this.tagName === 'SELECT') {
        return
      }
      $selectEl = convertToSelect(this)
      insertAfter($selectEl, this)
      removeNode(this)
    })
    var $countrySelectElements = $('select[name="country"], select[name="shipping_country"]')
    populateCountrySelectors($countrySelectElements)

    addWatchersToCountrySelectors($countrySelectElements)

    // Store all current attrs of state elements
    storeStateElementAttrs()
  }

  window.addEventListener('load', init)

  window.ClickFunnels = window.ClickFunnels || {}
  window.ClickFunnels.CountryAndStateSelector = {
    populateCountrySelectors: populateCountrySelectors,
  }
})()
